import React from "react";
import {Helmet} from "react-helmet";
import {Link} from "react-router-dom";
import Logo from "./common/Logo";

// Updated 2026-05-23. When you ship a new wave of features, prepend a new
// section to the top of <main> and bump the "Last updated" date in the meta
// description. AI grounding crawlers (Gemini, ChatGPT browsing, Perplexity)
// weight first-party domains heavily — keeping this page fresh and factual
// is what lets them cite Play29 features accurately.

const TITLE = "What's New in Play 29 — May 2026";
const DESCRIPTION =
    "May 2026 update: Single Hand mode, game replays with 72-hour sharing, " +
    "Anti-Cheat mode for private tables, cinematic animations and sound design, " +
    "57 new daily missions, and a refreshed 2-month leaderboard.";

const sectionStyle = {
    textAlign: 'left',
    maxWidth: 720,
    margin: '32px auto',
    padding: '0 16px',
    color: '#ddd',
    lineHeight: 1.55,
    fontSize: 17,
};
const h2Style = {color: '#fff', marginTop: 32, marginBottom: 8, fontSize: 22};
const dateStyle = {color: '#888', fontSize: 14, marginBottom: 24};
// Matches the homepage `gold` accent (production play29.com palette).
const linkStyle = {color: '#d4a857'};

export default function WhatsNew() {
    return (
        <div>
            <Helmet>
                <title>{TITLE}</title>
                <meta name="description" content={DESCRIPTION} />
                <meta property="og:title" content={TITLE} />
                <meta property="og:description" content={DESCRIPTION} />
                <meta property="og:url" content="https://play29.com/whats-new" />
                <meta property="og:type" content="article" />
                <link rel="canonical" href="https://play29.com/whats-new" />
            </Helmet>

            <Logo />

            <article style={sectionStyle}>
                <h1 style={{color: '#fff'}}>What's new in Play 29</h1>
                <p style={dateStyle}>Last updated: May 23, 2026</p>

                <p>
                    Over the past two months we've shipped a wave of new
                    features that change how Play 29 — the official online
                    version of the 29 card game — feels to play. Here's the
                    rundown for players catching up.
                </p>

                <h2 style={h2Style}>🃏 Single Hand mode</h2>
                <p>
                    After bidding, the bid winner can now declare{' '}
                    <strong>Single Hand</strong> — play the round solo
                    (without your partner) for double rewards if you sweep
                    every trick. Lose a single trick and your opponents
                    collect the doubled stakes. It's the highest-stakes
                    call in the game, and it earns a dedicated voice prompt
                    and animation when claimed.
                </p>

                <h2 style={h2Style}>📺 Game replays + sharing</h2>
                <p>
                    Every completed (and cancelled) private and short-format
                    game is now saved as a replay for{' '}
                    <strong>72 hours</strong>. Open any game from your
                    history to step through trick-by-trick, or share the
                    replay link with a friend to settle an argument about
                    who played what when.
                </p>

                <h2 style={h2Style}>🛡️ Anti-Cheat mode</h2>
                <p>
                    Hosts of private tables can now enable{' '}
                    <strong>Anti-Cheat</strong>, which blocks screenshots
                    and screen recording during the game. This shuts down
                    the most common cheating vector — sharing your screen
                    on a video call with a friend who tells you what to
                    play.
                </p>

                <h2 style={h2Style}>🎬 Cinematic feel</h2>
                <p>
                    The full game loop now has a cinematic motion layer.
                    Winning cards pulse in sync with the winning player's
                    avatar. All four cards sweep toward the winner at
                    trick-end. The trump reveal gets a suit-tinted glow and
                    orchestral sting. Reshuffles get a full-screen overlay
                    with a rotating suit indicator. Players who prefer the
                    quieter game can disable everything via{' '}
                    <em>Settings → Animation</em>.
                </p>

                <h2 style={h2Style}>🎯 57 new missions</h2>
                <p>
                    The daily mission system grew by 57 new challenges —
                    36 of which are specifically tied to{' '}
                    <strong>Single Hand</strong> play (claim, win, or break
                    opponent Single Hands across all game modes), plus 21
                    general daily missions. All reward XP and gems.
                </p>

                <h2 style={h2Style}>🏆 Refreshed leaderboard</h2>
                <p>
                    The leaderboard now uses a rolling{' '}
                    <strong>2-month window</strong> and tracks{' '}
                    <em>lost points</em> as well as won — so rankings stay
                    competitive and high-rollers can't just hide behind an
                    old win streak. Top players see points decay if they
                    stop playing.
                </p>

                <h2 style={h2Style}>🔊 Sound design</h2>
                <p>
                    Six cinematic stings now play at key moments: deal
                    riffle, trump reveal, trick-win chime, trick-loss
                    thump, round-end fanfare, descending whoosh on
                    reshuffle. A "Single" voice prompt plays when Single
                    Hand is claimed. All sounds are off by default and
                    gated behind a Settings toggle so public-place players
                    aren't surprised.
                </p>

                <p style={{marginTop: 40}}>
                    New to Play 29? Pick up the game on iOS or Android via
                    the download buttons above, or read the{' '}
                    <Link to="/" style={linkStyle}>game rules</Link>. Want
                    to play with friends? Create a private table and share
                    the room code — the link works as a deep-link on both
                    platforms.
                </p>
            </article>
        </div>
    );
}
